/**
 * The list of texts.
 *
 * No file dialog any more: there is nothing to select for which one would have
 * to give a location. Every text gets a row from the first word on, and the
 * sync carries it further.
 */

import { useEffect } from 'preact/hooks'
import { useSignal } from '@preact/signals'
import type { RefObject } from 'preact'
import * as st from '../state.js'
import * as store from '../editor/store.js'
import { apiKey } from '../editor/sync.js'
import { Panel, Action } from './Panel.js'
import { Icon } from './icons.js'
import { relativeTime } from './hooks.js'
import type { Doc } from '../types.js'

export function DocsPanel({
  anchor,
  onPick,
  onNew,
}: {
  anchor: RefObject<HTMLButtonElement>
  onPick: (d: Doc) => void
  onNew: () => void
}) {
  const open = st.panel.value === 'docs'
  const list = useSignal<Doc[]>([])

  // Load on opening, not in advance: the list changes while writing goes on,
  // and one held in the background would be out of date.
  useEffect(() => {
    if (!open) return
    let alive = true
    store.recent().then((d) => {
      if (alive) list.value = d
    })
    return () => { alive = false }
  }, [open, st.doc.value?.rev])

  const close = (): void => { st.panel.value = null }

  const remove = async (d: Doc, e: MouseEvent): Promise<void> => {
    e.stopPropagation()
    await store.deleteDoc(d.id)
    if (st.doc.value?.id === d.id) st.doc.value = null
    list.value = await store.recent()
  }

  return (
    <Panel
      open={open}
      title="Meine Texte"
      anchor={anchor}
      onClose={close}
      actions={
        // Only „Neuer Text". Downloading a .txt was from the time before the
        // texts lived on a server: what it was for — having them somewhere
        // else too — the sync does now, on every device and without a folder
        // full of files nobody opens again.
        <Action icon="file-plus" onClick={() => { close(); onNew() }}>
          Neuer Text
        </Action>
      }
      // Where the texts are kept when the app has its key. Without it they
      // stay on this device, and that must be said before a text is only
      // here.
      note={
        apiKey()
          ? undefined
          : 'Diese Texte liegen nur auf diesem Gerät. Öffne einmal den Link mit ' +
            '#k=…, dann sind sie auf allen Geräten da.'
      }
    >
      <ul class="docs-list">
        {list.value.map((d) => (
          <li key={d.id} class={`docs-item${d.id === st.doc.value?.id ? ' current' : ''}`}>
            <button class="docs-pick" onClick={() => { close(); onPick(d) }}>
              <span class="docs-name">
                {/* Where it came from. A transcript is a text like any other
                    and belongs in this list — but whoever looks for something
                    he typed should not have to open it to find out. */}
                {d.id.startsWith('foto-') && (
                  <span class="docs-from" title="Abfotografiert">
                    <Icon name="camera" size={13} />
                  </span>
                )}
                {d.title || 'Unbenannt'}
              </span>
              <span class="docs-when">{relativeTime(d.changed)}</span>
            </button>
            <button
              class="docs-remove"
              title="Aus der Liste nehmen"
              aria-label={`„${d.title || 'Unbenannt'}" aus der Liste nehmen`}
              onClick={(e) => void remove(d, e as MouseEvent)}
            >
              <Icon name="x" size={14} />
            </button>
          </li>
        ))}
        {!list.value.length && <li class="docs-empty">Noch keine Texte.</li>}
      </ul>
    </Panel>
  )
}
